import * as React from "react";
import * as _ from "underscore";
import { connect } from "react-redux";
import { Button, InlineNotification, Select, SelectItem, TextArea } from "carbon-components-react";
import { Warning20 } from "@carbon/icons-react";

import {
  deletePendingMessage as _deletePendingMessage,
  markMessageAsRead as _markMessageAsRead,
  markMessageAsUnread as _markMessageAsUnread,
  loadMessageTemplate as _loadMessageTemplate,
} from "web_modules/Messages/actions";
import MessageForm from "web_modules/Messages/MessageForm";

import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";

import MessageConversation from "./MessageConversation";
import { MessageTemplatesOptions } from "./values";

const styles = require("./styles.module.scss");

interface MessagesProps extends HurdleProps {
  unreadCount: number;
  messages: any;
  messageTemplate: string;
  templateName: string;
  errorDetail: string;
  missingFields: MissingField[];
  pendingMessages: any;
  documents: any;
  deletePendingMessage: Function;
  markMessageAsRead: Function;
  markMessageAsUnread: Function;
  loadMessageTemplate: Function;
}

interface MissingField {
  name: string;
  description: string;
}

class Messages extends React.Component<MessagesProps, any> {
  constructor(props) {
    super(props);
    this.state = {
      selectedMessageTemplateKey: "",
    };
    this.deletePendingMessage = this.deletePendingMessage.bind(this);
    this.markMessageAsRead = this.markMessageAsRead.bind(this);
    this.markLatestMessageAsUnread = this.markLatestMessageAsUnread.bind(this);
    this.resetMessagesState = this.resetMessagesState.bind(this);
    this.loadTemplate = this.loadTemplate.bind(this);
  }

  deletePendingMessage(jobID) {
    const {
      app: { id: appID },
      deletePendingMessage,
    } = this.props;
    deletePendingMessage(appID, jobID);
  }

  markMessageAsRead(messageID) {
    const {
      app: { id: appID },
      markMessageAsRead,
    } = this.props;
    markMessageAsRead(appID, messageID);
  }

  markLatestMessageAsUnread() {
    const {
      app: { id: appID },
      messages,
      markMessageAsUnread,
    } = this.props;
    const latestMessageID = messages[messages.length - 1].id;
    markMessageAsUnread(appID, latestMessageID);
  }

  resetMessagesState() {
    this.setState({ selectedMessageTemplateKey: "" });
  }

  loadTemplate() {
    const {
      app: { id: appID },
      loadMessageTemplate,
    } = this.props;
    loadMessageTemplate(appID, this.state.selectedMessageTemplateKey);
  }

  render() {
    const {
      messages,
      messageTemplate,
      templateName,

      errorDetail,
      missingFields,

      pendingMessages,
      documents,
      app,
      hurdle,
      onHide,
    } = this.props;
    const { selectedMessageTemplateKey } = this.state;
    const appID = app.id;

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <MessageConversation
          appID={appID}
          messages={messages}
          pendingMessages={pendingMessages}
          deletePendingMessage={this.deletePendingMessage}
          markMessageAsRead={this.markMessageAsRead}
        />
        <div className={styles.markMessagesUnreadButtonWrapper}>
          <div className={styles.selectMessageTemplateWrapper}>
            <Select
              id="message-template-selector"
              labelText=""
              value={selectedMessageTemplateKey}
              onChange={e => {
                this.setState({ selectedMessageTemplateKey: e.target.value }, this.loadTemplate);
              }}
              size="sm"
              className={styles.selectMessageTemplate}
            >
              <SelectItem value="" text="Select message template..." />
              {MessageTemplatesOptions}
            </Select>
            {errorDetail !== "" && (
              <p style={{ color: "red" }}>An error has occurred. Check below.</p>
            )}
          </div>
          <Button
            className={styles.markBorrowerMessagesUnreadButton}
            onClick={this.markLatestMessageAsUnread}
            kind="tertiary"
            type="submit"
            size="sm"
          >
            Mark Latest Borrower Message Unread
          </Button>
        </div>
        <div style={{ marginBottom: "20px" }} />
        {selectedMessageTemplateKey !== "" && messageTemplate !== "" ? (
          // MessageForm must be initialized with the right values to handle form state as-expected,
          //   so show a placeholder <textarea> until we have the correct values to mount MessageForm
          <MessageForm
            // Set unique-key prop to force remounting & reinitialization if switching to a diff template
            key={`${selectedMessageTemplateKey}-${messageTemplate}`}
            appID={appID}
            existingDocuments={documents}
            templateName={templateName}
            isOperatorContext
            defaultMessage={messageTemplate || ""}
            resetMessagesState={this.resetMessagesState}
          />
        ) : (
          <TextArea
            labelText=""
            disabled
            value={
              errorDetail === ""
                ? "Select a message template to continue composing a message"
                : "An error has occurred - please check below for more information"
            }
          />
        )}
        {errorDetail !== "" && (
          <div className={styles.messageTemplateErrorWrapper}>
            <p>
              <Warning20 style={{ marginRight: "0.5rem" }} fill="red" />
              Error: &nbsp;<span>{errorDetail}</span>
            </p>
            {missingFields.map(field => (
              <InlineNotification
                key={field.name}
                title={field.name}
                subtitle={field.description}
                kind="error"
                hideCloseButton
                lowContrast
              />
            ))}
          </div>
        )}
      </HurdleView>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const appID = ownProps.app.id;
  const messagesStore = state.messages[appID] || {};
  const messages = messagesStore.messages || [];
  const messageTemplate = messagesStore.messageTemplate || "";
  const templateName = messagesStore.templateName || "";
  const errorDetail = messagesStore.errorDetail || "";
  const missingFields = messagesStore.missingFields || [];
  let pendingMessages = messagesStore.pendingMessages || {};
  // this is sorta a hack -- since the message form uses the borrower doc uploader,
  // which triggers an action that updates the borrower state in redux
  const { loanApplication } = state.borrower;

  const application: any = _.findWhere(state.operator.loanApps, {
    id: appID,
  });

  pendingMessages = _.mapObject(pendingMessages, pendingMessage => {
    const hasDocs = pendingMessage && pendingMessage.documents;
    let newDocsForPendingMessage = hasDocs ? pendingMessage.documents : [];
    if (
      pendingMessage &&
      pendingMessage.document_ids &&
      pendingMessage.document_ids.length > 0 &&
      !pendingMessage.documents
    ) {
      newDocsForPendingMessage = loanApplication.documents.filter(
        doc => pendingMessage.document_ids.indexOf(doc.id) >= 0,
      );
    }
    const existingDocsForPendingMessage = application.documents.filter(
      doc =>
        pendingMessage.document_ids.indexOf(doc.id) >= 0 &&
        !_.findWhere(newDocsForPendingMessage, { id: doc.id }),
    );
    return {
      ...pendingMessage,
      documents: _.union(newDocsForPendingMessage, existingDocsForPendingMessage),
    };
  });

  return {
    messages,
    messageTemplate,
    templateName,
    errorDetail,
    missingFields,

    pendingMessages,
    documents: application.documents,
    unreadCount: messagesStore.unreadCount || 0,
  };
};

const mapDispatchToProps = dispatch => ({
  deletePendingMessage: (appID, jobID) => {
    dispatch(_deletePendingMessage(appID, jobID));
  },
  markMessageAsRead: (appID, messageID) => {
    dispatch(_markMessageAsRead(appID, messageID));
  },
  markMessageAsUnread: (appID, messageID) => {
    dispatch(_markMessageAsUnread(appID, messageID));
  },
  loadMessageTemplate: (appID, messageTemplateKey) => {
    dispatch(_loadMessageTemplate(appID, messageTemplateKey));
  },
});

export default connect<{}, {}, any>(mapStateToProps, mapDispatchToProps)(Messages);
